import React from "react";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";
import { Tooltip } from "@xsolla/xui-tooltip";
import { Avatar, AvatarProps } from "./Avatar";

type Theme = ReturnType<typeof useResolvedTheme>["theme"];

/** Item in the avatar group list */
export interface AvatarGroupItem {
  /** Image source URL */
  src?: string;
  /** Initials to display when no image is provided */
  initials?: string;
  /** Tooltip text to display on hover */
  tooltip?: string;
  /** Whether to show a badge on the avatar */
  badge?: boolean;
  /** Number or text to display in the badge notification */
  badgeCount?: React.ReactNode;
  /** Icon to display in the badge */
  badgeIcon?: React.ReactNode;
  /** Color tone for the badge */
  badgeTone?: AvatarProps["badgeTone"];
}

/** Avatar background mode - preset colors, 'mixed' for cycling, or a theme function */
export type AvatarBackgroundMode =
  | "mixed"
  | "brand"
  | "brandExtra"
  | "success"
  | "warning"
  | "alert"
  | "neutral"
  | ((theme: Theme) => string);

export interface AvatarGroupProps extends ThemeOverrideProps {
  /**
   * List of avatars to display in the group.
   * `tooltip` property is text to be displayed in the tooltip when hovering over the avatar.
   */
  list: AvatarGroupItem[];
  /** Size of the avatars in the group */
  size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Maximum number of slots rendered, including the "+N" overflow counter.
   * When the list exceeds this, (maxVisible - 1) avatars are shown plus a "+N" indicator.
   * When the list fits, all avatars are shown and no "+N" is rendered.
   */
  maxVisible?: number;
  /**
   * Controls the background color mode for avatars in the group.
   * - 'mixed' (default): Avatars cycle through different colors
   * - 'brand', 'brandExtra', 'success', 'warning', 'alert', 'neutral': Single color for all avatars
   * - Theme function: A function that receives theme and returns any color from the design system.
   *   Example: (theme) => theme.colors.core.link.link
   */
  avatarBackgroundMode?: AvatarBackgroundMode;
  /** Accessible label for the avatar group. Defaults to "Group of X users". */
  "aria-label"?: string;
  testID?: string;
}

const DEFAULT_MAX_VISIBLE = 6;

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  list,
  size = "sm",
  maxVisible = DEFAULT_MAX_VISIBLE,
  avatarBackgroundMode = "mixed",
  "aria-label": ariaLabel,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });

  if (maxVisible < 1) {
    console.warn(
      `[AvatarGroup] "maxVisible" must be a positive number. Received ${maxVisible}, falling back to default (${DEFAULT_MAX_VISIBLE}).`
    );
  }
  const effectiveMaxVisible = maxVisible < 1 ? DEFAULT_MAX_VISIBLE : maxVisible;

  const totalCount = list.length;
  const overflows = totalCount > effectiveMaxVisible;
  const visibleCount = overflows ? effectiveMaxVisible - 1 : totalCount;
  const visibleListItems = list.slice(0, visibleCount);
  const remainingCount = totalCount - visibleCount;

  // Overlap amount: 4px in Figma
  const overlap = -4;

  // Background colors for text/icon avatars (cycling through theme colors)
  const mixedBackgrounds = [
    theme.colors.background.alert.secondary,
    theme.colors.background.warning.secondary,
    theme.colors.background.brandExtra.secondary,
    theme.colors.background.neutral.secondary,
    theme.colors.background.brand.secondary,
    theme.colors.background.success.secondary,
    theme.colors.background.success.primary,
  ];

  const presetColors: Record<string, string> = {
    brand: theme.colors.background.brand.secondary,
    brandExtra: theme.colors.background.brandExtra.secondary,
    success: theme.colors.background.success.secondary,
    warning: theme.colors.background.warning.secondary,
    alert: theme.colors.background.alert.secondary,
    neutral: theme.colors.background.neutral.secondary,
  };

  const getBackgroundColor = (index: number): string => {
    if (typeof avatarBackgroundMode === "function") {
      return avatarBackgroundMode(theme);
    }
    if (avatarBackgroundMode === "mixed") {
      return mixedBackgrounds[index % mixedBackgrounds.length];
    }
    return (
      presetColors[avatarBackgroundMode] || theme.colors.background.secondary
    );
  };

  const defaultAriaLabel =
    totalCount === 1
      ? "1 user"
      : totalCount === visibleCount
        ? `${totalCount} users`
        : `${visibleCount} of ${totalCount} users`;

  const renderAvatarWithTooltip = (
    avatar: React.ReactNode,
    tooltip: string | undefined,
    key: number
  ) => {
    if (tooltip) {
      return (
        <Tooltip key={key} content={tooltip} placement="top">
          {avatar}
        </Tooltip>
      );
    }
    return avatar;
  };

  return (
    <Box
      testID={testID}
      flexDirection="row"
      alignItems="center"
      role="group"
      aria-label={ariaLabel || defaultAriaLabel}
    >
      {visibleListItems.map((item, index) => {
        const shouldApplyBackground = !item.src;
        const backgroundColor = shouldApplyBackground
          ? getBackgroundColor(index)
          : undefined;

        const avatar = (
          <Box
            key={index}
            marginLeft={index === 0 ? 0 : overlap}
            zIndex={totalCount - index}
          >
            <Avatar
              src={item.src}
              text={item.initials}
              size={size}
              backgroundColor={backgroundColor}
              disableHover={true}
              badge={item.badge}
              badgeCount={item.badgeCount}
              badgeIcon={item.badgeIcon}
              badgeTone={item.badgeTone}
              {...(item.tooltip ? { "aria-label": item.tooltip } : {})}
            />
          </Box>
        );

        return renderAvatarWithTooltip(avatar, item.tooltip, index);
      })}

      {remainingCount > 0 && (
        <Box marginLeft={overlap} zIndex={0}>
          <Avatar
            size={size}
            text={remainingCount > 99 ? "99+" : `+${remainingCount}`}
            backgroundColor={theme.colors.background.secondary}
            disableHover={true}
            aria-label={`${remainingCount} more ${remainingCount === 1 ? "user" : "users"}`}
          />
        </Box>
      )}
    </Box>
  );
};
